import { Link, useCanGoBack, useRouter } from '@tanstack/react-router';
import { CircleArrowLeft } from 'lucide-react';

import { useReportingParentOutstandingBalances } from '~entities/payment/api/reporting-parent-outstanding-balances';
import { useGetWalletBalance } from '~entities/wallet/api/get-wallet-balance';

import { TransactionsTable } from '~features/parents/ui/transactions/transactions-table';
import { ParentWalletTransactions } from '~features/students/ui/student-payment/parent-wallet-transactions';

import { useGetCurrency } from '~shared/api/currency-api';
import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';

interface TransactionsTableProps {
  parentId: string;
}

export const TransactionsTablePage = ({ parentId }: TransactionsTableProps) => {
  const router = useRouter();
  const canGoBack = useCanGoBack();

  const { data: outstandingBalance } = useReportingParentOutstandingBalances({
    parentId,
    format: 'json',
  });

  const { data: walletData } = useGetWalletBalance(parentId);
  const currencyData = useGetCurrency();

  return (
    <div className=''>
      <div className='mb-4'>
        {canGoBack ? (
          <Button
            variant={'ghost'}
            className='flex items-center p-2 [&_svg]:size-6'
            disabled={!canGoBack}
            onClick={() => {
              router.history.back();
            }}
          >
            <CircleArrowLeft className='size-6' />
            <h2 className='text-2xl font-semibold text-custom-secondary-1'>
              Total Transactions
            </h2>
          </Button>
        ) : (
          <Link
            to='/family'
            search={{
              parentId: parentId,
            }}
            className='flex w-fit items-center gap-2'
          >
            <CircleArrowLeft className='size-6' />
            <h2 className='text-2xl font-semibold text-custom-secondary-1'>
              Total Transactions
            </h2>
          </Link>
        )}
      </div>

      <div className='border-t pt-8'>
        <div className={cn('flex flex-col gap-6 md:mx-0 md:flex-row')}>
          <Card
            className={cn(
              "flex w-[calc(100vw-40px-40px)] flex-1 flex-col justify-between bg-[url('/card_1_bg.png')] bg-cover bg-center bg-no-repeat p-6 text-card-foreground md:w-80 lg:w-[32.375rem]",
            )}
          >
            <CardHeader
              className={cn('p-0 pb-2 text-base font-medium text-black')}
            >
              <CardTitle>Current Outstanding Balance</CardTitle>
            </CardHeader>
            <CardContent className={cn('flex justify-between p-0')}>
              <h2 className={cn('text-3xl font-semibold')}>
                {currencyData.data?.symbol ?? '$'}
                {typeof outstandingBalance !== 'string'
                  ? (outstandingBalance?.totalOutstanding ?? 0).toFixed(2)
                  : ''}
              </h2>
            </CardContent>
          </Card>
          <div className='flex-1 md:w-full'>
            <Card className='flex flex-col gap-6 p-6'>
              <CardHeader className='flex flex-row items-center gap-2 space-y-0 p-0 pb-2'>
                <CardTitle>Wallet Balance</CardTitle>
                <ParentWalletTransactions parentId={parentId} />
              </CardHeader>
              <CardContent className='flex p-0'>
                <div className='text-3xl font-bold'>
                  {currencyData.data?.symbol ?? '$'}
                  {(walletData?.walletBalance ?? 0).toFixed(2)}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <div className='mt-8'>
        <TransactionsTable parentId={parentId} />
      </div>
    </div>
  );
};
